/**
 * PHP-4126: a website shown from a runtime preview link (`portal.sitePreview` / `previewWhatsapp`),
 * at a desktop viewport scaled down to the box. The runtime allows only the platform apps to frame
 * these links; the site itself still refuses every frame.
 */
import { useLayoutEffect, useRef, useState } from "react";

import { Spinner } from "@/components/ui";

const VIEWPORT_WIDTH = 1280;
const VIEWPORT_HEIGHT = 800;

/** An https page cannot frame an http one (staff may switch site links to http). */
export function frameable(url: string): boolean {
  return !(url.startsWith("http:") && typeof window !== "undefined" && window.location.protocol === "https:");
}

export function LiveSiteFrame({
  url,
  title,
  interactive = false,
  fill = false,
}: {
  url: string;
  /** Names the frame for screen readers. */
  title: string;
  /** Clickable (the chat button opens WhatsApp); otherwise a picture that never takes focus. */
  interactive?: boolean;
  /** Cover a fixed-height box (top aligned) instead of taking the viewport's aspect ratio. */
  fill?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [loaded, setLoaded] = useState(false);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scale = size.width ? size.width / VIEWPORT_WIDTH : 0;
  // Filling a box: show as much of the page as the box is tall, never less than one viewport.
  const frameHeight = fill && scale ? Math.max(VIEWPORT_HEIGHT, size.height / scale) : VIEWPORT_HEIGHT;

  return (
    <div
      ref={box}
      className={
        fill ? "relative size-full overflow-hidden bg-surface" : "relative overflow-hidden bg-surface"
      }
      style={
        fill
          ? undefined
          : { height: VIEWPORT_HEIGHT * scale || undefined, aspectRatio: scale ? undefined : "16 / 10" }
      }
      inert={!interactive}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-fg-muted">
          <Spinner />
        </div>
      )}
      <iframe
        src={url}
        title={title}
        data-testid="live-site-frame"
        // Its own origin: scripts run, links and the chat button open new tabs, the portal page is untouchable.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        tabIndex={interactive ? undefined : -1}
        onLoad={() => setLoaded(true)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: VIEWPORT_WIDTH,
          height: frameHeight,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          visibility: scale ? "visible" : "hidden",
        }}
      />
    </div>
  );
}
